import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    ClickAwayListener,
    Container,
    Divider,
    Grid,
    Grow,
    makeStyles,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Typography,
    useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import GenericButton from "components/Buttons/GenericButton";
import PreviewData from "components/FormFields/PreviewData";
import LocationHeader from "components/Headers/LocationHeader";
import { getMapRentById } from "crud/rent.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useLocation, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { formatCurrency, formatPercentage } from "utils/string_utils";

import componentStyles from "assets/theme/components/dropdowns/navbar-menu-dropdown.js";
import PreviewDocument from "components/PreviewDocument";
import PreviewObservations from "components/PreviewObservations";
import {
    cancelMapRent,
    getForeignWorkerList,
    getNationalWorkerList,
    getPaymentDocuments,
    getRentCertificateDocumentById,
} from "crud/rent.crud";
import usePreviewDocument from "hooks/usePreviewDocument";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { DIALOG } from "utils/actionsConsts";
import { MAP_RENT_STATES } from "utils/const";
import CancelModal from "./CancelModal";
import EmployeeList from "./EmployeeList";
import PaymentModal from "./PaymentModal";
import WorkCentersList from "./WorkCentersList";

const useNavStyles = makeStyles(componentStyles);

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: "calc(110px + 2rem)",
    paddingBottom: "2rem",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: "2rem",
    gap: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),

    "& .MuiCard-root": {
      marginBottom: 0,
    },
  },
  contentHeader: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    flex: 1,
    backgroundColor: "#F9F9FA",
    padding: theme.spacing(2),
    gap: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      padding: theme.spacing(1.5),
      gap: theme.spacing(1),
    },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export default function RENTDetails() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { id } = useParams();
  const dispatch = useDispatch();
  const classes = useStyles();
  const navClasses = useNavStyles();

  const { data, isFetching } = useQuery(["getMapRentById"], () => getMapRentById(id), {
    enabled: !!id,
  });

  const [{ state }, handleDownloadRentCertificate] = usePreviewDocument(getRentCertificateDocumentById);
  const [{ state: foreignWorkerState }, handleForeignWorkerDownload] = usePreviewDocument(getForeignWorkerList);
  const [{ state: nationalWorkerState }, handleNationalWorkerDownload] = usePreviewDocument(getNationalWorkerList);

  const [modalOpen, setModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [openWorkersReport, setOpenWorkersReport] = useState(false);
  const [open, setOpen] = React.useState(false);

  const anchorRefWorkersReport = React.useRef(null);

  const form = useForm();

  const history = useHistory();
  const location = useLocation();

  const listBackLink = location?.state?.from || "/admin/rent/list";

  const employmentCenter = useMemo(() => data?.employmentCenter ?? {}, [data]);

  const employmentCenterDescription = useMemo(() => {
    if (!employmentCenter?.province || !employmentCenter?.description) return "";

    return `${employmentCenter.province} / ${employmentCenter.description}`;
  }, [employmentCenter]);

  function handleEditMap() {
    history.push(`/admin/rent/edit/${id}`);
  }

  const handleCloseWorkersReport = (event) => {
    if (anchorRefWorkersReport.current && anchorRefWorkersReport.current.contains(event.target)) {
      return;
    }
    setOpenWorkersReport(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  const handleToggleWorkersReport = () => {
    setOpenWorkersReport((prevOpenWorkersReport) => !prevOpenWorkersReport);
  };

  const { mutateAsync: cancelRENTMap, isLoading: isCancelling } = useMutation({
    mutationFn: async (motive) => {
      if (!id) {
        throw new Error("O id do mapa RENT não está definido.");
      }
      return await cancelMapRent({
        Id: id,
        motive,
      });
    },
    mutationKey: ["cancelMapRent"],
    onSuccess: () => {
      setCancelModalOpen(false);
      dispatch({
        type: DIALOG.SHOW,
        payload: {
          showGenericDialog: true,
          genericDialogData: {
            type: DIALOG.ONE_BUTTON_TYPE,
            isSuccess: true,
            buttonLabel: "Ok",
            message: "O mapa RENT foi cancelado com sucesso.",
          },
        },
      });
      history.push(listBackLink);
    },
    onError: (error) => {
      console.error("Mutation failed", error);
      const errorMessage = error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(". ")
        : error?.response?.data?.message || "Ocorreu um erro ao cancelar o mapa RENT.";
      dispatch({
        type: DIALOG.SHOW,
        payload: {
          showGenericDialog: true,
          genericDialogData: {
            type: DIALOG.ONE_BUTTON_TYPE,
            isSuccess: false,
            message: errorMessage,
          },
        },
      });
    },
  });

  const confirmationCancelMap = () => {
    setCancelModalOpen(true);
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      <LocationHeader
        iconButton={false}
        section="Detalhes"
        subsection="Lista de Mapas RENT"
        subsectionLink={listBackLink}
      />

      <header className={classes.header}>
        <Typography
          style={{
            textTransform: "uppercase",
          }}
          color="primary"
          variant="h1"
        >
          Detalhes do Mapa RENT
        </Typography>

        <Box
          display="flex"
          alignItems={isSmDown ? "stretch" : "center"}
          justifyContent={isSmDown ? "stretch" : "center"}
          flexDirection={isSmDown ? "column" : "row"}
          gridGap={12}
          width={isSmDown ? "100%" : "auto"}
        >
          <ConditionGuard condition={data?.stateKeyValue === MAP_RENT_STATES.AWAITING_PAYMENT}>
            <GenericButton
              onClick={() => setModalOpen(true)}
              color="tertiary"
              size={isSmDown ? "medium" : "small"}
              fullWidth={isSmDown}
            >
              Informações de Pagamento
            </GenericButton>
          </ConditionGuard>

          <ConditionGuard condition={data?.stateKeyValue === MAP_RENT_STATES.REJECTED}>
            <GenericButton
              onClick={handleEditMap}
              color="tertiary"
              size={isSmDown ? "medium" : "small"}
              fullWidth={isSmDown}
            >
              Editar Mapa RENT
            </GenericButton>
          </ConditionGuard>

          <ConditionGuard condition={data?.stateKeyValue === MAP_RENT_STATES.APPROVED}>
            <GenericButton
              loading={state.get(data?.certificate?.id)}
              color="tertiary"
              size={isSmDown ? "medium" : "small"}
              fullWidth={isSmDown}
              onClick={() => handleDownloadRentCertificate(data?.certificate?.id, data?.certificate?.documentName)}
            >
              Exportar Certificado
            </GenericButton>

            <div style={{ position: "relative", width: isSmDown ? "100%" : "auto" }}>
              <GenericButton
                loading={foreignWorkerState.get(id) || nationalWorkerState.get(id)}
                color="tertiary"
                size={isSmDown ? "medium" : "small"}
                fullWidth={isSmDown}
                onClick={handleToggleWorkersReport}
              >
                Lista de Trabalhadores
              </GenericButton>

              <Popper
                open={openWorkersReport}
                anchorEl={anchorRefWorkersReport.current}
                role={undefined}
                transition
                disablePortal
                style={{ position: "absolute" }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: placement === "bottom" ? "center top" : "center bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleCloseWorkersReport}>
                        <MenuList autoFocusItem={open} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                          <MenuItem
                            className={navClasses.dropItems}
                            onClick={() => (handleForeignWorkerDownload(id), handleCloseWorkersReport())}
                          >
                            Lista de Trabalhadores Estrangeiros
                          </MenuItem>
                          <MenuItem
                            className={navClasses.dropItems}
                            onClick={() => (handleNationalWorkerDownload(id), handleCloseWorkersReport())}
                          >
                            Lista de Trabalhadores Nacionais
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </div>
          </ConditionGuard>
        </Box>
      </header>

      <ConditionGuard
        condition={!isFetching}
        fallback={
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        }
      >
        <div className={classes.content}>
          <header className={classes.contentHeader}>
            <PreviewData title="Data de Submissão" subtitle={data?.submissionDate} />
            <PreviewData title="Nº do Mapa RENT" subtitle={id} />
            <PreviewData title="Estado" subtitle={data?.stateDescription} />
            <PreviewData title="Centro de Emprego" subtitle={employmentCenterDescription} />
            <PreviewData title="Tipo de Submissão" subtitle={data?.submissionType} />
          </header>

          <ConditionGuard condition={data?.stateKeyValue === MAP_RENT_STATES.CANCELLED}>
            <section className={classes.section}>
              <Typography color="primary" variant="h2">
                Cancelamento
              </Typography>

              <Card>
                <CardContent className={classes.content}>
                  <PreviewData title="Data de Cancelamento" subtitle={data?.cancellation?.date} />

                  <PreviewData title="Motivo de Cancelamento" subtitle={data?.cancellation?.motive} />
                </CardContent>
              </Card>
            </section>
          </ConditionGuard>

          <ConditionGuard condition={data?.stateKeyValue === MAP_RENT_STATES.REJECTED}>
            <section className={classes.section}>
              <Typography color="primary" variant="h2">
                Motivo da Rejeição
              </Typography>

              <Card>
                <CardContent className={classes.content}>
                  <PreviewData title="Motivo" subtitle={data?.rentRejectMotive?.motive} />

                  <PreviewData title="Observações" subtitle={data?.rentRejectMotive?.description} />
                </CardContent>
              </Card>
            </section>
          </ConditionGuard>

          <section className={classes.section}>
            <Typography color="primary" variant="h2">
              1. Informações da Empresa
            </Typography>

            <Card>
              <CardContent className={classes.content}>
                <Typography color="primary" variant="h4">
                  Dados da Empresa
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <PreviewData title="Nome" subtitle={data?.companyName} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <PreviewData title="NIF" subtitle={data?.companyTaxIdNumber} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <PreviewData title="NISS" subtitle={data?.companyNISS} />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <PreviewData title="Morada" subtitle={data?.companyAddress} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <PreviewData title="Província" subtitle={data?.companyProvince} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <PreviewData title="Município" subtitle={data?.companyMunicipality} />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <PreviewData title="E-mail" subtitle={data?.companyEmail} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <PreviewData title="Telefone" subtitle={data?.companyPhoneNumber} />
                  </Grid>
                </Grid>

                <Divider />

                <Typography color="primary" variant="h4">
                  Actividade e Prestação Social
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <PreviewData title="Actividade Principal da Empresa" subtitle={data?.companyActivity} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PreviewData title="Objecto Social" subtitle={data?.companyCorporateObject} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PreviewData title="Ano de Inicío da Actividade" subtitle={data?.companyActivityStartYear} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PreviewData title="Natureza Jurídica" subtitle={data?.companyLegalForm} />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <PreviewData
                      title="Trabalhadores ao Serviço na Última Semana de Março"
                      subtitle={data?.companyTotalWorkersNumber}
                    />
                  </Grid>
                </Grid>

                <Divider />

                <Typography color="primary" variant="h4">
                  Capital Social
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <PreviewData title="Capital Social" subtitle={formatCurrency(data?.companyShareCapital)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PreviewData
                      title="Repartição Estatal (%)"
                      subtitle={formatPercentage(data?.companyPublicDivisionPercentage)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PreviewData
                      title="Repartição Privado Nacional (%)"
                      subtitle={formatPercentage(data?.companyPrivateNationalDivisionPercentage)}
                    />
                  </Grid>
                </Grid>

                <ConditionGuard condition={data?.stateKeyValue !== MAP_RENT_STATES.AWAITING_PAYMENT}>
                  <Divider />

                  <Typography color="primary" variant="h4">
                    Comprovativo de Pagamento
                  </Typography>

                  <PreviewDocument promise={getPaymentDocuments} documents={data?.documents} />
                </ConditionGuard>
              </CardContent>
            </Card>
          </section>

          <FormProvider {...form}>
            <form>
              <section className={classes.section}>
                <Typography color="primary" variant="h2">
                  2. Dados de Centros de Trabalho
                </Typography>

                <WorkCentersList />
              </section>

              <section className={classes.section}>
                <Typography color="primary" variant="h2">
                  3. Dados de Trabalhadores
                </Typography>

                <EmployeeList />
              </section>
            </form>
          </FormProvider>

          <section className={classes.section}>
            <Typography color="primary" variant="h2">
              Observações
            </Typography>

            <Card>
              <CardContent className={classes.content}>
                <PreviewObservations observations={data?.observations} />
              </CardContent>
            </Card>
          </section>
        </div>
      </ConditionGuard>

      <Box
        display="flex"
        justifyContent={isSmDown ? "stretch" : "flex-end"}
        alignItems={isSmDown ? "stretch" : "center"}
        flexDirection={isSmDown ? "column" : "row"}
        gridGap={12}
        mt={4}
      >
        <ConditionGuard condition={data?.stateKeyValue === MAP_RENT_STATES.AWAITING_PAYMENT}>
          <GenericButton
            onClick={confirmationCancelMap}
            color="tertiaryRed"
            size={isSmDown ? "medium" : "large"}
            fullWidth={isSmDown}
          >
            Cancelar Submissão Mapa
          </GenericButton>
        </ConditionGuard>
        <GenericButton
          onClick={() => history.push(listBackLink)}
          color="tertiary"
          size={isSmDown ? "medium" : "large"}
          fullWidth={isSmDown}
        >
          Voltar à página inicial
        </GenericButton>
      </Box>

      <ConditionGuard condition={modalOpen}>
        <PaymentModal handleClosePaymentModal={() => setModalOpen(false)} isOpenPaymentModal={modalOpen} />
      </ConditionGuard>

      <CancelModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={(motive) => cancelRENTMap(motive)}
        isLoading={isCancelling}
      />
    </Container>
  );
}
